import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { ChartContainer, ChartTooltip, ChartTooltipContent, ChartConfig } from './ui/chart';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid } from 'recharts';
import { MetricType, TimeRange, MetricsHistoryPoint } from '../services/api';

interface MetricsChartProps {
  data: MetricsHistoryPoint[];
  metric: MetricType;
  range: TimeRange;
  loading?: boolean;
  error?: string | null;
}

const metricLabels: Record<MetricType, string> = {
  activeCustomers: 'Aktive Kunden',
  monthlyConversations: 'Monatliche Gespräche',
  totalCallMinutes: 'Gesamtminuten',
  activeLlmModels: 'Active LLM Models',
  apiCallsToday: 'API Calls Today',
};

const metricColors: Record<MetricType, string> = {
  activeCustomers: '#2563eb', // blue-600
  monthlyConversations: '#16a34a', // green-600
  totalCallMinutes: '#ea580c', // orange-600
  activeLlmModels: '#9333ea', // purple-600
  apiCallsToday: '#ca8a04', // yellow-600
};

function formatXAxisLabel(timestamp: string, range: TimeRange): string {
  const date = new Date(timestamp);

  if (range === 'day') {
    return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  }

  return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
}

function formatTooltipLabel(timestamp: string, range: TimeRange): string {
  const date = new Date(timestamp);

  if (range === 'day') {
    return date.toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit',
      day: '2-digit',
      month: '2-digit'
    });
  }

  return date.toLocaleDateString('de-DE', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
}

export function MetricsChart({ data, metric, range, loading, error }: MetricsChartProps) {
  const chartConfig: ChartConfig = {
    value: {
      label: metricLabels[metric],
      color: metricColors[metric],
    },
  };

  const chartData = data.map((point) => ({
    ...point,
    label: formatXAxisLabel(point.timestamp, range),
    tooltipLabel: formatTooltipLabel(point.timestamp, range),
  }));

  if (loading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">{metricLabels[metric]} - Verlauf</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center" style={{ height: '33.33vw', maxHeight: '300px' }}>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">{metricLabels[metric]} - Verlauf</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center" style={{ height: '33.33vw', maxHeight: '300px' }}>
            <p className="text-red-600">{error}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (data.length === 0) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">{metricLabels[metric]} - Verlauf</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center" style={{ height: '33.33vw', maxHeight: '300px' }}>
            <p className="text-gray-500">Keine Daten verfügbar</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg">{metricLabels[metric]} - Verlauf</CardTitle>
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig} className="w-full" style={{ height: '33.33vw', maxHeight: '300px' }}>
          <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id={`gradient-${metric}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={metricColors[metric]} stopOpacity={0.3} />
                <stop offset="95%" stopColor={metricColors[metric]} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="label"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              fontSize={12}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              fontSize={12}
              tickFormatter={(value) => value.toLocaleString()}
            />
            <ChartTooltip
              content={
                <ChartTooltipContent
                  labelFormatter={(_, payload) => {
                    if (payload && payload.length > 0) {
                      return payload[0].payload.tooltipLabel;
                    }
                    return '';
                  }}
                />
              }
            />
            <Area
              type="monotone"
              dataKey="value"
              stroke={metricColors[metric]}
              strokeWidth={2}
              fill={`url(#gradient-${metric})`}
            />
          </AreaChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}
